import * as S from "./planCard.style";
import Button from "~/spocketUI/components/Button";
import Separator from "~/spocketUI/components/Separator";
import { SVG } from "~/spocketUI/components/SVG";
import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons";
import { SVGIcon } from "~/spocketUI/components/SVG/types";
import { Trans, useTranslation } from "react-i18next";
import { IPlan, SubscriptionType } from "~/types/billing";
import { centsToDecimal, getTotalPlanCost } from "~/spocketUI/helpers/numbers";
import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { triggerShowResumeModal } from "~/spocketUI/helpers/customEvents";
import { IAccount } from "~/types/account";
import { useQueryClient } from "@tanstack/react-query";
import { PRORATION } from "~/api/billing/types";
import { paths } from "~/router/paths";
import FlexContainer from "~/spocketUI/components/FlexContainer";

type Props = {
  plan?: IPlan;
  previousPlanName: string | null;
  account: IAccount | null;
  closeModal?: () => void;
};

const splitNumber = (number: number | string) => {
  const fixed = Number(number);
  const integerPart = Math.floor(fixed);
  const decimalPart = (fixed - integerPart).toFixed(2).slice(1);

  return { integer: integerPart, decimal: decimalPart };
};

const PlanCard = ({ plan, account, previousPlanName, closeModal }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAnnual = plan?.interval === SubscriptionType.ANNUAL;
  const bgColor = plan?.is_highlighted || isAnnual ? "primary" : "transparent";
  const textColor = plan?.is_highlighted || isAnnual ? "white" : "primary";
  const isSubscribed = plan?.id === account?.active_subscription?.plan?.id;
  const buttonText = useMemo(() => {
    if (isSubscribed && account?.active_subscription?.paused_at)
      return t("settings.paused");
    if (isSubscribed) return t("settings.current");
    return t("settings.start");
  }, [isAnnual, isSubscribed, account?.active_subscription?.plan]);
  const queryClient = useQueryClient();

  const handleRedirect = () => {
    if (account?.active_subscription?.paused_at)
      return triggerShowResumeModal();
    localStorage.setItem("plansModal", "false");

    if (!account) {
      const planId = plan?.id?.toString();
      const url = paths.app.checkout.replace(
        ":company_id",
        window.location.href.split("/").pop() as string
      );
      return navigate(url + (planId ? `?plan_id=${planId}` : ""));
    }

    queryClient.invalidateQueries(PRORATION);
    closeModal && closeModal();

    const companyId = window.location.href.includes("?company_id=")
      ? window.location.href.split("?company_id=").pop()
      : null;
    if (companyId) navigate(`/checkout/${plan?.id}?company_id=${companyId}`);
    else navigate(`/checkout/${plan?.id}`);
  };

  const splitedTotal = splitNumber(
    centsToDecimal(plan?.cost_per_month as number).toFixed(2)
  );

  return (
    <S.PlanCardContainer
      primary={plan?.is_highlighted || false}
      data-testid="plan-card">
      {isAnnual || plan?.is_highlighted ? (
        <S.PlanCardPromotion isHighlighted={plan?.is_highlighted}>
          {isAnnual ? (
            <S.MonthsOffText>
              {plan?.is_highlighted ? "🔥 " : null}
              <Trans
                i18nKey="settings.months_off"
                values={{ days: plan?.months_off }}
              />
            </S.MonthsOffText>
          ) : plan?.is_highlighted ? (
            <S.MostPopularText>🔥 {t("settings.popular")}</S.MostPopularText>
          ) : null}
        </S.PlanCardPromotion>
      ) : null}

      <FlexContainer
        flexDirection="column"
        alignItems="flex-start"
        justifyContent="center"
        gap={0.7}
        padding={!isAnnual ? "7px 7px 14px 7px" : "7px"}>
        <S.PlanCardTitle>{plan?.name}</S.PlanCardTitle>
        <S.PlanCardPrice>
          {plan?.old_cost_per_month ? (
            <S.DiscountText color={plan?.is_highlighted ? "white" : "primary"}>
              ${centsToDecimal(plan?.old_cost_per_month)}
            </S.DiscountText>
          ) : null}
          <Trans
            i18nKey="settings.mon"
            components={{
              1: <S.MonthlyValue isHighlighted={plan?.is_highlighted} />,
              2: <S.DecimalValue />,
            }}
            values={{
              integer: splitedTotal.integer,
              decimal: plan?.old_cost_per_month ? "" : splitedTotal.decimal,
            }}
          />
        </S.PlanCardPrice>
        {isAnnual && (
          <S.PlanBilledAnnualText isHighlighted={plan?.is_highlighted}>
            <Trans
              i18nKey={"settings.billed_annually"}
              values={{
                amount: `$${getTotalPlanCost(plan)}`,
              }}
            />
          </S.PlanBilledAnnualText>
        )}
      </FlexContainer>
      <Separator type="horizontal" className="separator" />
      <S.ButtonContainer isHighlighted={plan?.is_highlighted}>
        <Button
          size="xl"
          long
          bgColor={bgColor}
          width="100%"
          color={textColor}
          fontWeight={500}
          isDisabled={!!isSubscribed}
          onClick={handleRedirect}
          radius={0.4}>
          {buttonText}
        </Button>
      </S.ButtonContainer>
      <S.PlanCardList data-testid="plan-features">
        <S.EverythingPLusText>
          {previousPlanName
            ? t("settings.everything_plus", { planName: previousPlanName })
            : t("settings.what_you_get")}
        </S.EverythingPLusText>
        {plan?.features.map((feature: string, i: number) => (
          <S.PlanCardItem key={`${feature}-${i}`}>
            <SVG
              icon={faCheckCircle as SVGIcon}
              color={plan.is_highlighted ? "white" : "primary"}
              size="lg"
            />
            <S.FeatureText dangerouslySetInnerHTML={{ __html: feature }} />
          </S.PlanCardItem>
        ))}
      </S.PlanCardList>
    </S.PlanCardContainer>
  );
};

export default PlanCard;
